import { Injectable, computed, signal, type Provider } from '@angular/core';
import { flairForPoints, type BadgeDefinitionDto } from '../../core/badges/badge-domain';
import { makeError, type ApiError } from '../../core/models/api-result';
import { PlatformDataService } from '../../core/services/platform-data.service';
import {
  PublicProfileService,
  type Contributions,
  type PublicBadgeRow,
  type PublicPerson,
} from '../../core/services/public-profile.service';
import type { PublicPresence } from '../../core/utils/public-presence';

/**
 * `/preview/people/:id`: the full profile page (/people/:id) without a session, on a sample colleague, so its look
 * can be reviewed and tested in every theme. Nothing is sent.
 *
 *   ?state=loading|error|missing|quiet   still loading, a failed read, nobody there, or no badges or contributions
 *   ?presence=office|booked|home|none    today (default office)
 *   ?theme=graphite|workplace-slate|workplace-paper   the paper, set on the page only (never the profile)
 *
 * The sample is Nath's screenshot of 4 Oct: 110 points, seven badges, one post.
 */

const THEMES = ['graphite', 'workplace-slate', 'workplace-paper'];

const badge = (id: string, tier: string, points: number, awardedAt: string): PublicBadgeRow => ({
  definition: { id, domain: id.split('-')[0], tier, icon: id, target: 1, points, series: null, stage: null, secret: false, retired: false } satisfies BadgeDefinitionDto,
  awardedAt,
});

const BADGES: readonly PublicBadgeRow[] = [
  badge('games-top-ten', 'gold', 50, '2026-09-30T09:00:00Z'),
  badge('community-first-join', 'bronze', 10, '2026-09-02T09:00:00Z'),
  badge('workplace-first-room', 'bronze', 10, '2026-09-04T09:00:00Z'),
  badge('workplace-first-booking', 'bronze', 10, '2026-09-01T09:00:00Z'),
  badge('workplace-first-desk', 'bronze', 10, '2026-09-01T09:00:00Z'),
  badge('community-first-post', 'bronze', 10, '2026-09-12T09:00:00Z'),
  badge('games-first-game', 'bronze', 10, '2026-09-08T09:00:00Z'),
];

const CONTRIBUTIONS: Contributions = {
  postCount: 1,
  questionCount: 0,
  answerCount: 0,
  acceptedAnswerCount: 0,
  votesReceived: 3,
  recent: [{ id: 'post-1', title: 'What we learned moving the studio to the second floor', format: 'post', contentType: 'article', communityId: null, createdAt: '2026-09-12T09:00:00Z' }],
};

const PRESENCE: Readonly<Record<string, PublicPresence | null>> = {
  office: { checkedIn: true, hasBooking: true, locationId: 'loc-rum-runner', statusId: null, statusName: null },
  booked: { checkedIn: false, hasBooking: true, locationId: 'loc-rum-runner', statusId: null, statusName: null },
  home: { checkedIn: false, hasBooking: false, locationId: null, statusId: 'wfh', statusName: 'Working from home' },
  none: null,
};

function query(): URLSearchParams {
  const hash = typeof location !== 'undefined' ? location.hash : '';
  return new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
}

/** PublicProfileService's signals, answered from the sample. */
@Injectable()
export class PreviewPublicProfileService {
  private readonly params = query();
  private readonly state = this.params.get('state');
  private readonly _person = signal<PublicPerson | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<ApiError | null>(null);
  private readonly quiet = this.state === 'quiet';

  readonly person = this._person.asReadonly();
  readonly personLoading = this._loading.asReadonly();
  readonly personError = this._error.asReadonly();
  private readonly _badges = signal<PublicBadgeRow[]>([]);
  readonly badgeRows = this._badges.asReadonly();
  readonly badgesLoading = signal(false).asReadonly();
  readonly points = computed(() => this._badges().reduce((sum, row) => sum + row.definition.points, 0));
  readonly flair = computed(() => flairForPoints(this.points()));
  private readonly _contributions = signal<Contributions | null>(null);
  readonly contributions = this._contributions.asReadonly();
  readonly contributionsLoading = signal(false).asReadonly();
  private readonly _presence = signal<PublicPresence | null>(null);
  readonly presence = this._presence.asReadonly();

  constructor() {
    const theme = this.params.get('theme');
    if (theme && THEMES.includes(theme)) document.documentElement.setAttribute('data-theme', theme);
  }

  async load(userId: string): Promise<void> {
    this._error.set(null);
    if (this.state === 'loading') {
      this._loading.set(true);
      return;
    }
    if (this.state === 'error') {
      this._error.set(makeError('NETWORK', 'The profile could not be loaded.'));
      return;
    }
    if (this.state === 'missing') {
      this._person.set(null);
      return;
    }
    this._person.set({
      _id: userId,
      name: 'Peter Löfgren',
      email: 'peter.lofgren@ricoh.example',
      picture: null,
      jobTitle: 'Workplace Experience Lead',
      department: 'Ricoh RDx · Workplace',
      homeLocationId: 'loc-rum-runner',
      homeLocation: { _id: 'loc-rum-runner', name: 'Rum Runner Works', timezoneId: 'Europe/London', timezone: { tzTimezone: 'Europe/London' } },
      hideFromPresence: false,
      brandColorOverride: null,
    });
    this._presence.set(PRESENCE[this.params.get('presence') ?? 'office'] ?? null);
    this._badges.set(this.quiet ? [] : [...BADGES]);
    this._contributions.set(this.quiet ? null : CONTRIBUTIONS);
  }

  async refreshPresence(): Promise<void> {}
}

/** The route's providers: the sample profile, and the one office it names. */
export function publicProfilePreviewProviders(): Provider[] {
  return [
    PreviewPublicProfileService,
    { provide: PublicProfileService, useExisting: PreviewPublicProfileService },
    {
      provide: PlatformDataService,
      useValue: {
        locationsList: signal([{ _id: 'loc-rum-runner', name: 'Rum Runner Works' }]).asReadonly(),
        loadLocations: async () => undefined,
      },
    },
  ];
}
